// Local Api facade compatible with axios-like api used in pages
import { listAccounts, createAccount, updateAccount, removeAccount } from './db/repositories/accounts.ts';
import { listCategories, createCategory, updateCategory, removeCategory } from './db/repositories/categories.ts';
import { listTransactions, createTransaction, updateTransaction, removeTransaction, markPaid, markUnpaid } from './db/repositories/transactions.ts';
import { listTasks, createTask, updateTask, removeTask } from './db/repositories/tasks.ts';
import { generateRecurringTransactionsForMonth } from './recurringTransactionGenerator.ts';
import { createRecurringTransaction, updateRecurringTransaction, removeRecurringTransaction } from './db/repositories/recurringTransactions.ts';
import { listObjectives, createObjective, updateObjective, getObjectiveById } from './db/repositories/okrObjectives.ts';
import { listKeyResultsByObjective, createKeyResult, updateKeyResult } from './db/repositories/okrKeyResults.ts';
import { listIndicatorsByKeyResult, createIndicator, updateIndicator, getIndicatorByKeyResultMonth } from './db/repositories/okrIndicators.ts';
import { listInvestmentPlans, createInvestmentPlan, updateInvestmentPlan, removeInvestmentPlan } from './db/repositories/investmentPlans.ts';
import { listInvestmentLedgers, createInvestmentLedger } from './db/repositories/investmentLedgers.ts';
import { listRecordsByLedger, createInvestmentRecord } from './db/repositories/investmentRecords.ts';
import { listScenariosByPlan, createInvestmentScenario } from './db/repositories/investmentScenarios.ts';
import { createInvestmentAllocation } from './db/repositories/investmentAllocations.ts';
import { listInvestmentGoals, createInvestmentGoal } from './db/repositories/investmentGoals.ts';

function ok(data: any) { return Promise.resolve({ data }); }

function computeInstallmentAmount(totalAmount: number, installments: number, installmentNumber: number): number {
  if (!installments || installments <= 0 || !installmentNumber || installmentNumber <= 0) return totalAmount;
  const totalCents = Math.round(totalAmount * 100);
  const base = Math.floor(totalCents / installments);
  const remainder = totalCents % installments;
  const amountCents = base + (installmentNumber <= remainder ? 1 : 0);
  return amountCents / 100;
}

function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}

async function generateAllInstallmentMonths(userId: number, startDateStr: string, installments: number) {
  const base = parseLocalDate(startDateStr);
  for (let i = 0; i < installments; i++) {
    const monthDate = new Date(base.getFullYear(), base.getMonth() + i, 1);
    await generateRecurringTransactionsForMonth(userId, monthDate.getMonth() + 1, monthDate.getFullYear());
  }
}

async function buildObjectiveDetail(localObjectiveId: number) {
  const objective = await getObjectiveById(localObjectiveId);
  if (!objective) return null;
  const keyResults = await listKeyResultsByObjective(objective.id);
  const enrichedKeyResults = await Promise.all(
    keyResults.map(async (kr) => {
      const indicators = await listIndicatorsByKeyResult(kr.id);
      const latest = indicators
        .slice()
        .sort((a, b) => (a.month || '').localeCompare(b.month || ''))
        .pop();
      const currentValue = latest?.value ?? 0;
      const progressPercent = kr.target_value ? (currentValue / kr.target_value) * 100 : 0;
      return {
        ...kr,
        current_value: currentValue,
        progress_percent: progressPercent,
      };
    })
  );
  return {
    ...objective,
    key_results: enrichedKeyResults,
  };
}

export const localApi = {
  get: async (url: string, config?: any) => {
    try {
      if (url.startsWith('/api/v1/accounts')) return ok(await listAccounts());
      if (url.startsWith('/api/v1/categories')) return ok(await listCategories());
      if (url.startsWith('/api/v1/transactions')) {
        const competence = config?.params?.competence as string | undefined;
        
        // Se há competência especificada, tentar gerar transações recorrentes
        if (competence) {
          try {
            // Parse competência (MM/YYYY)
            const [monthStr, yearStr] = competence.split('/');
            const month = parseInt(monthStr, 10);
            const year = parseInt(yearStr, 10);
            
            if (!isNaN(month) && !isNaN(year)) {
              // Obter userId do localStorage ou usar 1 como padrão para modo offline
              const offlineUser = localStorage.getItem('offline_user');
              const userId = offlineUser ? JSON.parse(offlineUser).id : 1;
              
              // Gerar transações recorrentes para o mês
              await generateRecurringTransactionsForMonth(userId, month, year);
            }
          } catch (error) {
            console.error('[localApi] Erro ao gerar transações recorrentes:', error);
            // Continuar mesmo se houver erro na geração
          }
        }
        
        return ok(await listTransactions(competence));
      }
      if (url.startsWith('/api/v1/tasks')) {
        if (url.includes('/quadrant/')) {
          const quadrant = Number(url.split('/quadrant/')[1]);
          const allTasks = await listTasks();
          const filtered = allTasks.filter(t => {
            const isUrgent = t.is_urgent;
            const isImportant = t.is_important;
            if (quadrant === 1) return isUrgent && isImportant;
            if (quadrant === 2) return !isUrgent && isImportant;
            if (quadrant === 3) return isUrgent && !isImportant;
            if (quadrant === 4) return !isUrgent && !isImportant;
            return false;
          });
          return ok(filtered);
        }
        if (url.includes('/upcoming')) {
          const allTasks = await listTasks();
          const today = new Date();
          const days = Number(config?.params?.days || 3);
          const deadline = new Date(today);
          deadline.setDate(today.getDate() + days);
          const filtered = allTasks.filter(t => {
            const dueDate = new Date(t.due_date);
            return dueDate <= deadline && t.status === 'pending';
          });
          return ok(filtered);
        }
        if (url.includes('/urgent-important')) {
          const allTasks = await listTasks();
          const filtered = allTasks.filter(t => t.is_urgent && t.is_important && t.status === 'pending');
          return ok(filtered);
        }
        return ok(await listTasks());
      }
      if (url.startsWith('/api/v1/objectives')) {
        const match = url.match(/\/api\/v1\/objectives\/(\d+)/);
        if (match) {
          const objectiveId = Number(match[1]);
          return ok(await buildObjectiveDetail(objectiveId));
        }
        const all = await listObjectives();
        const periodType = config?.params?.period_type as string | undefined;
        const status = config?.params?.status as string | undefined;
        const filtered = all.filter((objective) => {
          if (periodType && objective.period_type !== periodType) return false;
          if (status && objective.status !== status) return false;
          return true;
        });
        const detailed = await Promise.all(filtered.map((objective) => buildObjectiveDetail(objective.id)));
        return ok(detailed.filter(Boolean));
      }
      if (url.startsWith('/api/v1/key-results') && url.includes('/indicators')) {
        const keyResultId = Number(url.split('/key-results/')[1]?.split('/')[0]);
        if (!Number.isFinite(keyResultId)) return ok([]);
        return ok(await listIndicatorsByKeyResult(keyResultId));
      }
      if (url.startsWith('/api/v1/investment-plans')) {
        return ok(await listInvestmentPlans());
      }
      if (url.startsWith('/api/v1/investment-tracking/ledgers')) {
        return ok(await listInvestmentLedgers());
      }
      if (url.startsWith('/api/v1/investment-tracking/records')) {
        const ledgerIdParam = url.includes('ledger_id=') ? url.split('ledger_id=')[1] : '';
        const ledgerId = Number(ledgerIdParam?.split('&')[0]);
        if (!Number.isFinite(ledgerId)) return ok([]);
        return ok(await listRecordsByLedger(ledgerId));
      }
      if (url.startsWith('/api/v1/investment-advisory/scenarios')) {
        const planIdParam = url.includes('plan_id=') ? url.split('plan_id=')[1] : '';
        const planId = Number(planIdParam?.split('&')[0]);
        if (!Number.isFinite(planId)) return ok([]);
        return ok(await listScenariosByPlan(planId));
      }
      if (url.startsWith('/api/v1/investment-advisory/goals')) {
        return ok(await listInvestmentGoals());
      }
      if (url.startsWith('/api/v1/auth/profile')) {
        const stored = localStorage.getItem('offline_user');
        return ok(stored ? JSON.parse(stored) : null);
      }
      return ok(null);
    } catch (error) {
      console.error('[localApi] Erro ao buscar dados locais:', error);
      // Retorna array vazio ou null para evitar quebrar a aplicação
      if (url.startsWith('/api/v1/accounts') || url.startsWith('/api/v1/categories') || url.startsWith('/api/v1/transactions') || url.startsWith('/api/v1/tasks')) {
        return ok([]);
      }
      return ok(null);
    }
  },
  post: async (url: string, body: any) => {
    if (url === '/api/v1/accounts') return ok(await createAccount(body));
    if (url === '/api/v1/categories') return ok(await createCategory(body));
    if (url === '/api/v1/objectives') {
      const objective = await createObjective({
        title: body.title,
        description: body.description,
        period_type: body.period_type,
        period_start: body.period_start,
        period_end: body.period_end,
        status: 'active',
      });
      if (Array.isArray(body.key_results)) {
        for (const kr of body.key_results) {
          await createKeyResult({
            objective_id: objective.id,
            title: kr.title,
            target_value: kr.target_value,
            unit: kr.unit,
            status: 'active',
          });
        }
      }
      return ok(objective);
    }
    if (url.startsWith('/api/v1/objectives/') && url.includes('/key-results')) {
      const objectiveId = Number(url.split('/api/v1/objectives/')[1]?.split('/')[0]);
      if (!Number.isFinite(objectiveId)) return ok(null);
      return ok(await createKeyResult({
        objective_id: objectiveId,
        title: body.title,
        target_value: body.target_value,
        unit: body.unit,
        status: 'active',
      }));
    }
    if (url.startsWith('/api/v1/key-results') && url.includes('/indicators')) {
      const keyResultId = Number(url.split('/key-results/')[1]?.split('/')[0]);
      if (!Number.isFinite(keyResultId)) return ok(null);
      const existing = await getIndicatorByKeyResultMonth(keyResultId, body.month);
      if (existing) {
        const updated = await updateIndicator(existing.id, { value: body.value });
        return ok(updated);
      }
      return ok(await createIndicator({ key_result_id: keyResultId, month: body.month, value: body.value }));
    }
    if (url === '/api/v1/transactions') {
      // Se for transação recorrente, criar a recorrência primeiro
      if (body.is_recurring) {
        const offlineUser = localStorage.getItem('offline_user');
        const userId = offlineUser ? JSON.parse(offlineUser).id : 1;
        
        const recurrenceType = body.recurrence_type || 'fixed_monthly';

        const recurringData = {
          user_id: userId,
          description: body.description,
          amount: body.amount,
          type: body.type,
          account_id: body.account_id,
          category_id: body.category_id,
          recurrence_type: recurrenceType,
          start_date: body.due_date,
          end_date: recurrenceType === 'installment' ? undefined : (body.end_date || undefined),
          installments: body.installments || undefined,
          is_active: true
        };
        
        const recurring = await createRecurringTransaction(recurringData);
        
        // Criar a primeira transação também
        const transactionBody: any = {
          ...body,
          recurring_transaction_id: recurring.id,
          is_recurring: true
        };

        if (recurrenceType === 'installment' && body.installments && body.installments > 0) {
          transactionBody.installment_number = 1;
          transactionBody.amount = computeInstallmentAmount(body.amount, body.installments, 1);
          delete transactionBody.end_date;
        }

        const transaction = await createTransaction(transactionBody);

        // Parceladas: gerar TODAS as parcelas imediatamente
        if (recurrenceType === 'installment' && body.installments && body.installments > 0) {
          await generateAllInstallmentMonths(userId, recurringData.start_date, body.installments);
        }
        
        return ok(transaction);
      }
      return ok(await createTransaction(body));
    }
    if (url === '/api/v1/recurring-transactions') {
      // Criar transação recorrente
      const offlineUser = localStorage.getItem('offline_user');
      const userId = offlineUser ? JSON.parse(offlineUser).id : 1;
      
      const recurrenceType = body.recurrence_type || 'fixed_monthly';

      const recurringData = {
        user_id: userId,
        description: body.description,
        amount: body.amount,
        type: body.type,
        account_id: body.account_id,
        category_id: body.category_id,
        recurrence_type: recurrenceType,
        start_date: body.start_date || body.due_date,
        end_date: recurrenceType === 'installment' ? undefined : (body.end_date || undefined),
        installments: body.installments || undefined,
        is_active: true
      };
      
      const recurring = await createRecurringTransaction(recurringData);
      
      // Gerar transações do mês de início (e, se parcelada, de todos os meses)
      if (recurrenceType === 'installment' && body.installments && body.installments > 0) {
        await generateAllInstallmentMonths(userId, recurringData.start_date, body.installments);
      } else {
        const startDate = parseLocalDate(recurringData.start_date);
        const month = startDate.getMonth() + 1;
        const year = startDate.getFullYear();
        await generateRecurringTransactionsForMonth(userId, month, year);
      }
      
      return ok(recurring);
    }
    if (url.match(/\/api\/v1\/recurring-transactions\/(\d+)\/delete-scope$/)) {
      const id = Number(url.split('/').slice(-2)[0]);
      const scope = body?.scope as 'all' | 'future';
      const fromDueDate = body?.from_due_date as string | undefined; // YYYY-MM-DD

      if (scope !== 'all' && scope !== 'future') {
        throw new Error('Invalid scope');
      }
      if (scope === 'future' && !fromDueDate) {
        throw new Error('from_due_date is required for scope=future');
      }

      // Remover transações locais vinculadas conforme escopo
      const allTx = await listTransactions();
      const related = allTx.filter(t => (t as any).recurring_transaction_id === id);
      const toDelete = scope === 'all'
        ? related
        : related.filter(t => t.due_date >= (fromDueDate as string));

      for (const t of toDelete) {
        await removeTransaction(t.id);
      }

      if (scope === 'all') {
        await removeRecurringTransaction(id);
      } else {
        // Encerrar a recorrência para impedir geração lazy futura
        const d = parseLocalDate(fromDueDate as string);
        d.setDate(d.getDate() - 1);
        const endDateStr = d.toISOString().slice(0, 10);
        await updateRecurringTransaction(id, { is_active: false, end_date: endDateStr });
      }

      return ok({ message: 'Recurring transaction deleted successfully' });
    }
    if (url === '/api/v1/investment-plans') return ok(await createInvestmentPlan(body));
    if (url === '/api/v1/investment-tracking/ledgers') return ok(await createInvestmentLedger(body));
    if (url === '/api/v1/investment-tracking/records') return ok(await createInvestmentRecord(body));
    if (url === '/api/v1/investment-advisory/scenarios') return ok(await createInvestmentScenario(body));
    if (url === '/api/v1/investment-advisory/allocation') {
      const allocation = await createInvestmentAllocation(body);
      return ok({ ...allocation, rebalancing_alerts: [] });
    }
    if (url === '/api/v1/investment-advisory/goals') return ok(await createInvestmentGoal(body));
    if (url === '/api/v1/tasks') return ok(await createTask(body));
    if (url === '/api/v1/auth/login' || url === '/api/v1/auth/register') {
      const user = { id: 1, email: body.email || 'offline@device', name: body.name || 'Offline User', is_admin: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
      localStorage.setItem('offline_user', JSON.stringify(user));
      return ok({ token: 'offline-token', user });
    }
    return ok(null);
  },
  put: async (url: string, body?: any) => {
    if (url.match(/\/api\/v1\/accounts\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateAccount(id, body));
    }
    if (url.match(/\/api\/v1\/categories\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateCategory(id, body));
    }
    if (url.match(/\/api\/v1\/transactions\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateTransaction(id, body));
    }
    if (url.match(/\/api\/v1\/transactions\/(\d+)\/mark-paid/)) {
      const id = Number(url.split('/').slice(-2)[0]);
      return ok(await markPaid(id));
    }
    if (url.match(/\/api\/v1\/transactions\/(\d+)\/mark-unpaid/)) {
      const id = Number(url.split('/').slice(-2)[0]);
      return ok(await markUnpaid(id));
    }
    if (url.match(/\/api\/v1\/tasks\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateTask(id, body));
    }
    if (url.match(/\/api\/v1\/tasks\/(\d+)\/complete/)) {
      const id = Number(url.split('/').slice(-2)[0]);
      return ok(await updateTask(id, { status: 'completed' }));
    }
    if (url.match(/\/api\/v1\/tasks\/(\d+)\/uncomplete/)) {
      const id = Number(url.split('/').slice(-2)[0]);
      return ok(await updateTask(id, { status: 'pending' }));
    }
    if (url.match(/\/api\/v1\/recurring-transactions\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateRecurringTransaction(id, body));
    }
    if (url.match(/\/api\/v1\/objectives\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateObjective(id, body));
    }
    if (url.match(/\/api\/v1\/key-results\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateKeyResult(id, body));
    }
    if (url.match(/\/api\/v1\/investment-plans\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateInvestmentPlan(id, body));
    }
    return ok(null);
  },
  patch: async (url: string, body?: any) => {
    if (url.match(/\/api\/v1\/accounts\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateAccount(id, body));
    }
    if (url.match(/\/api\/v1\/categories\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateCategory(id, body));
    }
    if (url.match(/\/api\/v1\/transactions\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateTransaction(id, body));
    }
    if (url.match(/\/api\/v1\/tasks\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateTask(id, body));
    }
    if (url.match(/\/api\/v1\/recurring-transactions\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateRecurringTransaction(id, body));
    }
    if (url.match(/\/api\/v1\/objectives\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateObjective(id, body));
    }
    if (url.match(/\/api\/v1\/key-results\/(\d+)$/)) {
      const id = Number(url.split('/').pop());
      return ok(await updateKeyResult(id, body));
    }
    return ok(null);
  },
  delete: async (url: string) => {
    if (url.match(/\/api\/v1\/accounts\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      await removeAccount(id); return ok({});
    }
    if (url.match(/\/api\/v1\/categories\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      await removeCategory(id); return ok({});
    }
    if (url.match(/\/api\/v1\/transactions\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      await removeTransaction(id); return ok({});
    }
    if (url.match(/\/api\/v1\/tasks\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      await removeTask(id); return ok({});
    }
    if (url.match(/\/api\/v1\/recurring-transactions\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      await removeRecurringTransaction(id); return ok({});
    }
    if (url.match(/\/api\/v1\/investment-plans\/(\d+)/)) {
      const id = Number(url.split('/').pop());
      await removeInvestmentPlan(id); return ok({});
    }
    return ok({});
  }
};

export type ApiLike = typeof localApi;


